import dayjs from "dayjs"

import { globalStore } from "@/App"
import { Delivery, Order, OrderPoint, PackageType, Pickup } from "@/types"
import { MapLayer } from "@/views/MapView/partials/MapLayers"
import { OrdersPanelTab, OrdersStatsTab } from "@/views/MapView/partials/Orders/OrdersPanel"

import { DELIGOO_PACKAGE_TYPES, ECOMMERCE_PACKAGE_TYPES, HEBE_PACKAGE_TYPES, INPOST_PACKAGE_TYPES } from "./consts"
import { escapeRegex, formatTime } from "./utils"

export namespace OrderUtils {
  export function isUnassigned(order: Order | OrderPoint) {
    return order.state === "pending"
  }

  export function isAssigned(order: Order | OrderPoint) {
    return order.state === "assigned" || order.state === "published" || order.state === "started"
  }

  export function isInProgress(order: Order | OrderPoint) {
    return order.state === "started" || order.state === "in_progress"
  }

  export function isFinished(order: Order | OrderPoint) {
    return order.state === "finished" || order.state === "canceled" || order.state === "failed"
  }

  export function isPickupOnly(order: Order) {
    return order.pickup && !order.delivery
  }

  export function isDeliveryOnly(order: Order) {
    return order.delivery && !order.pickup
  }

  export function getCurrentPoint(order: Order) {
    if (OrderUtils.isPickupOnly(order)) return order.pickup!
    if (OrderUtils.isDeliveryOnly(order)) return order.delivery!

    return OrderUtils.isFinished(order.pickup!) ? order.delivery! : order.pickup!
  }

  export function getCurrentPointType(order: Order) {
    return OrderUtils.getCurrentPoint(order).type === "Order::Pickup" ? "pickup" : "delivery"
  }

  export function getCurrentState(order: Order) {
    if (OrderUtils.isPickupOnly(order)) return order.pickup!.state
    if (OrderUtils.isDeliveryOnly(order)) return order.delivery!.state

    const isPicked = order.pickup && order.pickup.state === "finished"
    const isDelivered = order.delivery && order.delivery.state === "finished"

    return isDelivered ? order.state : isPicked ? order.delivery!.state : order.pickup!.state
  }

  export function getProgress(order: Order) {
    if (OrderUtils.isFinished(order)) return 100
    if (order.delivery?.state === "in_progress") return 90
    if (order.delivery?.state === "started") return 70

    if (order.pickup?.state === "finished") return 50
    if (order.pickup?.state === "in_progress") return 40
    if (order.pickup?.state === "started") return 25

    return 0
  }

  export function getTime(order: Order) {
    // for sorting purposes
    if (OrderUtils.isUnassigned(order) && order.perform_at) return order.perform_at
    if (order.pickup?.estimated_time) return order.pickup.estimated_time
    return order.created_at
  }

  export function getPointCounter(order: Order) {
    let total = 0
    if (order.pickup) total++
    if (order.delivery) total++

    let count = 0
    if (order.pickup?.state === "finished") count++
    if (order.delivery?.state === "finished") count++

    return `${count}/${total}`
  }

  export function getPointTime(point: OrderPoint) {
    if (!point) return null

    return point.estimated_time
  }

  export function getPointDelay(point: OrderPoint) {
    if (!point) return NaN

    const { state, late } = point
    if (state === "canceled" || state === "failed") return NaN
    return late
  }

  export function getPointDelayColor(delay: OrderPoint["late"]) {
    if (!delay) return "info"
    return delay > 15 ? "alert" : delay > 0 ? "warning" : "info"
  }

  export function shouldShowTime(order: Order | OrderPoint) {
    return order.state !== "pending"
  }

  export function shouldShowDelay(point: OrderPoint, delay: OrderPoint["late"]) {
    return (
      (["assigned", "published", "started", "in_progress"].includes(point.state) && delay) || point.state === "finished"
    )
  }

  export function getPointEstimatedTimeWithDuration(point: OrderPoint) {
    if (!point.estimated_time) return null

    return dayjs(point.estimated_time).add(point.duration, "seconds")
  }

  export function getEstimatedDeliveryTime(order: Order) {
    if (!order.pickup || !order.delivery || !order.delivery.estimated_time) return null

    const pickupEstimatedTimeWithDuration = OrderUtils.getPointEstimatedTimeWithDuration(order.pickup)

    if (!pickupEstimatedTimeWithDuration) return null

    return dayjs(order.delivery.estimated_time).diff(pickupEstimatedTimeWithDuration.set("seconds", 0), "minutes")
  }

  export function getRealDeliveryTime(order: Order) {
    if (!order.pickup || !order.pickup.finished_at || !order.delivery || !order.delivery.finished_at) return null

    return dayjs(order.delivery.finished_at).diff(order.pickup.finished_at, "minutes")
  }

  export function isPointPickup(point: OrderPoint): point is Pickup {
    return point.type === "Order::Pickup"
  }

  export function isPointDelivery(point: OrderPoint): point is Delivery {
    return point.type === "Order::Delivery"
  }

  export function getTab(order: Order) {
    if (OrderUtils.isUnassigned(order)) return "unassigned"
    if (OrderUtils.isAssigned(order)) return "assigned"
    if (OrderUtils.isFinished(order)) return "finished"
    return undefined
  }

  export function filterByTab(order: Order, tab: OrdersPanelTab) {
    return (
      (OrderUtils.isUnassigned(order) && tab === "unassigned") ||
      (OrderUtils.isAssigned(order) && tab === "assigned") ||
      (OrderUtils.isFinished(order) && tab === "finished") ||
      tab === null // Show all orders if no tab is selected
    )
  }

  export function filterBySearch(order: Order, searchValue: string) {
    if (searchValue) {
      const searchValues = escapeRegex(searchValue).trim().split(" ")

      const regExps = searchValues.map((searchValue) => new RegExp(searchValue, "i"))

      return regExps.every(
        (regex) =>
          regex.test(order.uid) ||
          (order.returned_order_uid && regex.test(order.returned_order_uid)) ||
          (order.pickup &&
            (regex.test(order.pickup.phone) ||
              (order.pickup.name && regex.test(order.pickup.name)) ||
              regex.test(order.pickup.address))) ||
          (order.delivery && (regex.test(order.delivery.phone) || regex.test(order.delivery.address))) ||
          (order.driver && (regex.test(order.driver.name) || regex.test(String(order.driver.phone))))
      )
    }
    return true
  }

  export function filterByLayers(order: Order, layers: MapLayer[]) {
    const shouldHideCanceled = !layers.includes("canceled_orders")
    if (shouldHideCanceled && order.state === "canceled") return false

    const shouldHideFailed = !layers.includes("failed_orders")
    if (shouldHideFailed && order.state === "failed") return false

    const shouldHideFinished = !layers.includes("finished_orders")
    if (shouldHideFinished && order.state === "finished") return false

    return true
  }

  export function filterByStats(order: Order, tab: OrdersStatsTab) {
    const {
      teamsStats: { entities: teamsStats },
    } = globalStore.getState()

    const teamStats = teamsStats[order.team_id]
    if (!teamStats) return false

    const { omitted_orders_ids } = teamStats

    switch (tab) {
      case "omitted":
        return omitted_orders_ids.includes(order.id)
    }
  }

  export function isSinglePointOrder(order: Order) {
    return !order.pickup || !order.delivery
  }

  export function canReturnOrder(order: Order) {
    return !order.returned && ["finished", "failed"].includes(order.state)
  }

  export function canMoveToBuffer(order: Order) {
    const { state, buffering_disabled, is_buffered } = order

    if (state === "finished") return false
    if (is_buffered) return false
    if (buffering_disabled) return false

    return true
  }

  export function canAssignOrder(order: Order) {
    return order.is_buffered
  }

  export function getOrderPoints(order: Order) {
    if (OrderUtils.isSinglePointOrder(order)) {
      return order.pickup! || order.delivery!
    }

    return [order.pickup!, order.delivery!]
  }

  export function getPackagesCount(packages: Order["packages"] | undefined) {
    return packages ? Object.values(packages).reduce((prev, curr) => Number(prev) + Number(curr), 0) : 0
  }

  export function getOrderDriverLate(order: Order) {
    if (!order.pickup?.estimated_time || !order.pickup?.arrived_at) return null

    return dayjs(order.pickup.estimated_time).diff(dayjs(order.pickup.arrived_at), "minutes")
  }

  export function getOrderClientLate(order: Order) {
    if (!order.pickup?.estimated_time || !order.pickup?.finished_at) return null

    return dayjs(order.pickup.estimated_time).diff(dayjs(order.pickup.finished_at), "minutes")
  }

  export function getRealOrderDistance(order: Order) {
    const { distance_for_bicycle, distance_for_car, distance_for_scooter } = order

    const transportType = order.driver?.transport_type

    if (transportType === "bicycle" && distance_for_bicycle) {
      return distance_for_bicycle
    }

    if (transportType === "scooter" && distance_for_scooter) {
      return distance_for_scooter
    }

    if (transportType === "car" && distance_for_car) {
      return distance_for_car
    }

    return order.distance
  }

  export function getPickingTime(order: Order) {
    const minutes = order.pickup_in !== null ? `${order.pickup_in} min.` : "-"
    const time = order.pickup_at && `(${formatTime(order.pickup_at)})`

    return `${minutes} ${time}`
  }

  export function getInterfacePackageTypes(interfaceType: Order["client_interface_type"]): PackageType[] {
    switch (interfaceType) {
      case "deligoo":
        return [...DELIGOO_PACKAGE_TYPES]
      case "soliddelivery":
        return [...DELIGOO_PACKAGE_TYPES]
      case "ecommerce":
        return [...ECOMMERCE_PACKAGE_TYPES]
      case "hebe":
        return [...HEBE_PACKAGE_TYPES]
      case "inpost":
        return [...INPOST_PACKAGE_TYPES]
    }
  }
}
